import React, { useEffect, useState } from "react";
import {
  customSelectStyles,
  SelectOption,
} from "../../exports/customSelectStyles";
import Select from "react-select";
import { useLoader } from "../../context/LoaderContext";
import { dynamicFailed, dynamicSuccess } from "../../hooks/useToast";
import {
  createSecuritySettings,
  updateSecuritySettings,
} from "../../api/SettingsApi";
import { ISecuritySettings } from "../../exports/interfaces";
import { usePermission } from "../../hooks/usePermission";

interface SecuritySettingProps {
  securitySettings: ISecuritySettings | null;
  setSecuritySettings: React.Dispatch<
    React.SetStateAction<ISecuritySettings | null>
  >;
}

const SecuritySetting = ({
  securitySettings,
  setSecuritySettings,
}: SecuritySettingProps) => {
  const { setLoading } = useLoader();
  const { canEdit } = usePermission();
  const [security, setSecurity] = useState<ISecuritySettings>({
    minLength: 8,
    passwordExpiry: "30",
    requireUppercase: false,
    requireNumber: false,
    requireSpecialCharacter: false,
    preventPasswordReuse: false,
    require2FA: false,
    allowSmsFallback: false,
  });

  const handleToggle = (key: keyof ISecuritySettings) => {
    const updated = {
      ...security,
      [key]: !security[key],
    } as ISecuritySettings;
    setSecurity(updated);
    // setSecuritySettings(updated);
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>,
  ) => {
    const { name, value } = e.target;

    const updated = {
      ...security,
      [name]: value === "" ? "" : Number(value),
    } as ISecuritySettings;

    setSecurity(updated);
  };

  const passwordPolicies = [
    {
      key: "requireUppercase",
      title: "Require Uppercase Letter",
      description: "At least one A-Z character.",
    },
    {
      key: "requireNumber",
      title: "Require Number",
      description: "At least one 0-9 character.",
    },
    {
      key: "requireSpecialCharacter",
      title: "Require Special Character",
      description: "At least one symbol such as !@#$%.",
    },
    {
      key: "preventPasswordReuse",
      title: "Prevent Password Reuse",
      description: "Block the last 5 passwords.",
    },
  ];

  const twoFactorSettings = [
    {
      key: "require2FA",
      title: "Require 2FA for all Admins",
      description:
        "Admins will be prompted to set up an authenticator app on next sign in.",
    },
    {
      key: "allowSmsFallback",
      title: "Allow SMS as 2FA Fallback",
      description: "Less secure. Disable to require authenticator apps only.",
    },
  ];

  const passwordExpiryOptions = [
    { value: "7", label: "7 Days" },
    { value: "14", label: "14 Days" },
    { value: "30", label: "30 Days" },
    { value: "90", label: "90 Days" },
  ];

  const handleSelectChange = (
    name: keyof any,
    selected: SelectOption | null,
  ) => {
    const updated = {
      ...security,
      [name]: selected?.value,
    } as ISecuritySettings;

    setSecurity(updated);
    // setSecuritySettings(updated);
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      const payload = {
        ...security,
        minLength: security.minLength === "" ? 0 : security.minLength,
      };

      const response = securitySettings?.id
        ? await updateSecuritySettings(securitySettings.id, payload)
        : await createSecuritySettings(payload);

      if (response.status === 200 || response.status === 201) {
        setSecuritySettings(response.data);
        setSecurity(response.data);
        dynamicSuccess(
          securitySettings
            ? "Security settings updated successfully!"
            : "Security settings saved successfully!",
        );
      }
    } catch (error: any) {
      dynamicFailed(`${error.message}`);
    } finally {
      setTimeout(() => {
        setLoading(false);
      }, 1000);
    }
  };

  useEffect(() => {
    if (securitySettings) {
      setSecurity(securitySettings);
    }
  }, [securitySettings]);

  return (
    <>
      {/* Password Policy */}
      <div className="dashboard-card" style={{ borderTopLeftRadius: 0 }}>
        <div className="row g-3">
          <div className="col-md-6">
            <h5 className="mb-1">Password Policy</h5>
            <small className="text-secondary">
              Enforce these rules for all user accounts.
            </small>

            <div className="row mt-3">
              <div className="col-md-6">
                <label className="form-label">Minimum Length</label>
                <input
                  type="number"
                  className="form-control"
                  name="minLength"
                  value={security.minLength}
                  min={0}
                  onChange={handleChange}
                />
                {/* <small className="setting-description">
                  Minimum number of characters required for passwords
                </small> */}
              </div>

              <div className="col-md-6">
                <label className="form-label">Password Expiry (days)</label>
                <Select
                  styles={customSelectStyles}
                  options={passwordExpiryOptions}
                  placeholder="Select expiry duration"
                  value={
                    passwordExpiryOptions.find(
                      (option) => option.value === security.passwordExpiry,
                    ) || null
                  }
                  onChange={(selected) =>
                    handleSelectChange("passwordExpiry", selected)
                  }
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                />
                {/* <small className="setting-description">
                  How often users must change their password
                </small> */}
              </div>
            </div>

            <div className="row mt-2">
              {passwordPolicies.map((item) => (
                <div className="col-md-10" key={item.key}>
                  <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                    <div>
                      <label className="form-label d-block">{item.title}</label>
                      <small className="setting-description">
                        {item.description}
                      </small>
                    </div>

                    <div className="form-check form-switch notification-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          (security[
                            item.key as keyof ISecuritySettings
                          ] as boolean) ?? false
                        }
                        onChange={() =>
                          handleToggle(item.key as keyof ISecuritySettings)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Two Factor Authentication */}
      <div className="dashboard-card mt-2">
        <div className="row g-3">
          <div className="col-md-6">
            <h5 className="mb-1">Two-Factor Authentication</h5>
            <small className="text-secondary">
              Enhanced security for user accounts.
            </small>

            <div className="row mt-2">
              {twoFactorSettings.map((item) => (
                <div className="col-md-10" key={item.key}>
                  <div className="d-flex justify-content-between align-items-center py-3 border-bottom">
                    <div>
                      <label className="form-label d-block">{item.title}</label>
                      <small className="setting-description">
                        {item.description}
                      </small>
                    </div>

                    <div className="form-check form-switch notification-switch">
                      <input
                        className="form-check-input"
                        type="checkbox"
                        checked={
                          (security[
                            item.key as keyof ISecuritySettings
                          ] as boolean) ?? false
                        }
                        onChange={() =>
                          handleToggle(item.key as keyof ISecuritySettings)
                        }
                      />
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-4">
              <button
                className="btn btn-badge-primary px-3"
                onClick={handleSubmit}
                disabled={!canEdit("Settings")}
                style={{
                  pointerEvents: canEdit("Settings") ? "auto" : "none",
                  opacity: canEdit("Settings") ? 1 : 0.5,
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SecuritySetting;
